/**
 * Chess rules engine implementation wrapping chess.js.
 * This is the single source of truth for move legality and game state.
 * Used by both client and server to ensure congruence.
 */
import { Chess } from 'chess.js';
/**
 * Parse a UCI move string into components.
 * UCI format: <from><to>[promotion]
 * Examples: 'e2e4', 'e7e8q'
 */
function parseUCI(uci) {
    if (uci.length < 4 || uci.length > 5) {
        return null;
    }
    const from = uci.slice(0, 2);
    const to = uci.slice(2, 4);
    const promotion = uci.length === 5 ? uci[4] : undefined;
    // Basic validation of square format
    if (!/^[a-h][1-8]$/.test(from) || !/^[a-h][1-8]$/.test(to)) {
        return null;
    }
    if (promotion && !/^[qrbn]$/.test(promotion)) {
        return null;
    }
    return { from, to, promotion };
}
/**
 * Convert a chess.js move to UCI format.
 */
function toUCI(from, to, promotion) {
    return promotion ? `${from}${to}${promotion}` : `${from}${to}`;
}
/**
 * Create a new rules engine instance.
 * @param fen Optional starting position (defaults to standard starting position)
 */
export function createEngine(fen) {
    const chess = new Chess(fen);
    return {
        load(newFen) {
            chess.load(newFen);
        },
        fen() {
            return chess.fen();
        },
        turn() {
            return chess.turn();
        },
        isLegalMove(uci) {
            const parsed = parseUCI(uci);
            if (!parsed)
                return false;
            try {
                // Try the move on a copy to check legality
                const testChess = new Chess(chess.fen());
                const result = testChess.move({
                    from: parsed.from,
                    to: parsed.to,
                    promotion: parsed.promotion,
                });
                return result !== null;
            }
            catch {
                return false;
            }
        },
        makeMove(uci) {
            const parsed = parseUCI(uci);
            if (!parsed)
                return null;
            try {
                const result = chess.move({
                    from: parsed.from,
                    to: parsed.to,
                    promotion: parsed.promotion,
                });
                if (!result)
                    return null;
                return {
                    fen: chess.fen(),
                    isCheck: chess.isCheck(),
                    isCheckmate: chess.isCheckmate(),
                    isStalemate: chess.isStalemate(),
                    isGameOver: chess.isGameOver(),
                    san: result.san,
                };
            }
            catch {
                return null;
            }
        },
        getLegalMovesFrom(square) {
            try {
                const moves = chess.moves({ square: square, verbose: true });
                return moves.map(m => toUCI(m.from, m.to, m.promotion));
            }
            catch {
                return [];
            }
        },
        getAllLegalMoves() {
            const moves = chess.moves({ verbose: true });
            return moves.map(m => toUCI(m.from, m.to, m.promotion));
        },
        isCheck() {
            return chess.isCheck();
        },
        isCheckmate() {
            return chess.isCheckmate();
        },
        isStalemate() {
            return chess.isStalemate();
        },
        isGameOver() {
            return chess.isGameOver();
        },
        getSAN(uci) {
            const parsed = parseUCI(uci);
            if (!parsed)
                return null;
            try {
                // Peek at the move on a copy
                const testChess = new Chess(chess.fen());
                const result = testChess.move({
                    from: parsed.from,
                    to: parsed.to,
                    promotion: parsed.promotion,
                });
                return result ? result.san : null;
            }
            catch {
                return null;
            }
        },
    };
}
//# sourceMappingURL=engine.js.map